import {Template} from "../../../../components/template/template.jsx";
import React, {useCallback, useMemo, useRef, useState} from "react";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaFileExcel, FaPlus, FaSearch} from "react-icons/fa";
import {useFormNavigation} from "../../../../hooks/use-form-navigation.js";
import {useGeneralInput, useGeneralInputSpecialCase} from "../../../../hooks/use-general-input.js";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {
    vehicleMaintenanceStatusArr,
    vehicleStatusArr, vehicleStatusObj,
    vehicleTypeArr,
    vehicleTypeObj
} from "../utils/vehicles-utils.js";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../utils/ag-grid-columns-utils.js";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import {FaCircleExclamation, FaTriangleExclamation} from "react-icons/fa6";
import { v4 as uuidv4 } from "uuid";
import vehiclesService from "../../../../services/web/vehicles-service.js";
import {MaskDefault} from "../../../../utils/mask.js";
import projectUtils from "../../../../utils/project-utils.js";
import vehicleClassesService from "../../../../services/web/vehicle-classes-service.js";
import ShowHideColumnsGrid from "../../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";


const initialFilter = {
    prefix: "",
    status: "",
    plate: "",
    brand: "",
    model: "",
    type: "",
    vehicle_class: "",
    next_km_revision: ""
};

const buildFilter = (values) => {
    const filter = [];

    if (values.prefix?.trim()) filter.push({field: "prefix", type: "like", value: values.prefix.trim()});
    if (values.status) filter.push({field: "status", type: "like", value: values.status});
    if (values.plate?.trim()) filter.push({field: "plate", type: "like", value: values.plate.replace(/-/g, "").trim()});
    if (values.brand?.trim()) filter.push({field: "brand", type: "like", value: values.brand.trim()});
    if (values.model?.trim()) filter.push({field: "model", type: "like", value: values.model.trim()});
    if (values.type) filter.push({field: "type", type: "=", value: values.type});
    if (values.vehicle_class?.trim()) filter.push({field: "vehicle_class", type: "like", value: values.vehicle_class.trim()});
    if (values.next_km_revision) filter.push({field: "next_km_revision", type: "like", value: values.next_km_revision});

    return filter;
};

const pathModel = {
    create: {
        dev: `/vehicles/form`,
        isBlank: false
    },
    edit: {
        dev: `/vehicles/form`,
        isBlank: true
    }
}

const storageItem = "vehicles-hidden-columns";
const orderStorageItem = "vehicles-order-columns";
const defaultHiddenColumns = ["brand", "model"];

const getStoredColumns = (item, fallback) => {
    try {
        const value = JSON.parse(localStorage.getItem(item));
        return Array.isArray(value) ? value : fallback;
    } catch {
        localStorage.removeItem(item);
        return fallback;
    }
};

const VehiclesList = () => {
    const [filterValues, setFilterValues] = useState(initialFilter);
    const [filter, setFilter] = useState(buildFilter(initialFilter));
    const handleInput = useGeneralInput(setFilterValues);
    const handleSpecialInput = useGeneralInputSpecialCase(setFilterValues);
    const { onRowClicked, openForm } = useFormNavigation(pathModel);
    const gridApi = useRef(null);
    const [hiddenColumns, setHiddenColumns] = useState(
        () => getStoredColumns(storageItem, defaultHiddenColumns)
    );
    const [orderColumns, setOrderColumns] = useState(
        () => getStoredColumns(orderStorageItem, null)
    );

    const onColumnMoved = (params) => {
        const columns = params.api.getAllDisplayedColumns().map(column => column.colId);
        localStorage.setItem(orderStorageItem, JSON.stringify(columns));
        setOrderColumns(columns);
    };

    const toggleColumn = (field) => {
        setHiddenColumns((previousColumns) =>
            previousColumns.includes(field)
                ? previousColumns.filter(column => column !== field)
                : [...previousColumns, field]
        );
    };

    const {data: vehicleClasses = [], isLoading: isLoadingVehicleClasses} = useQuery({
        queryFn: vehicleClassesService.getAllToSelect,
        queryKey: ["classes"],
        select: data => data.map(el => ({
            value: el.id,
            label: el.text
        }))
    })

    const columnDefs = useMemo(() => {
        const columns = [
        {headerName: "Prefixo", field: "prefix", flex: 1, filter: true,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Status", field: "status", flex: 1, filter: true,
            valueGetter: ({data}) => vehicleStatusObj[data.status] ?? null,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Placa", field: "plate", flex: 1, filter: true,
            cellRenderer: ({value}) =>
                <div className="h-full w-full flex items-center font-bold">{projectUtils.formatPlate(value)}</div>,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Marca", field: "brand", flex: 1, filter: true,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Modelo", field: "model", flex: 1, filter: true,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Tipo", field: "type", flex: 1, filter: true,
            valueGetter: ({data}) => vehicleTypeObj[data.type],
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Km atual", field: "actual_km", flex: 1, filter: true,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Classe", field: "vehicle_classes_id", flex: 1, filter: true,
            valueGetter: ({data}) => {
                const value = data.vehicle_classes_id
                return vehicleClasses.find(el => el.value === value)?.label ?? null
            },
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {headerName: "Manutenção", field: "next_km_revision", flex: 1, filter: true,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            },
            valueFormatter: (params) => {
                const map = {
                    expire: "Vencida",
                    toexpire: "A vencer"
                };

                return map[params.value] ?? "";
            },
            useValueFormatterForExport: true,
            cellRenderer: ({value}) => {
                if(!value) return null;

                return <div className="flex gap-2 items-center">
                    {value === "expire"
                        ? <><FaTriangleExclamation color="#ed5565" size={16}/> <span className="text-[#ed5565]">Vencida</span></>
                        : <><FaCircleExclamation color="#f8ac59" size={16}/> <span className="text-[#f8ac59]">A vencer</span></>
                    }
                </div>
            }},
        ];

        const columnsWithVisibility = columns.map((column) => ({
            ...column,
            hide: hiddenColumns.includes(column.field)
        }));

        if (orderColumns) aggridUtils.sortColumns(columnsWithVisibility, orderColumns);

        return columnsWithVisibility;
    }, [hiddenColumns, orderColumns, vehicleClasses]);

    const {data: vehicles = [], isLoading: isLoadingVehicles} = useQuery({
        queryFn: () => vehiclesService.list(filter),
        queryKey: ['vehicles', {filter}],
        placeholderData: keepPreviousData,
    })

    const search = useCallback(() => {
        setFilter(buildFilter(filterValues));
    }, [filterValues]);

    const clean = useCallback(() => {
        setFilterValues(initialFilter);
        setFilter(buildFilter(initialFilter));
    }, []);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clean);

    const exportData = () => {
        gridApi.current.exportDataAsCsv({
            fileName: `${uuidv4()}.csv`,
            columnSeparator: ';',
        });
    };

    return(
        <Template isLoading={isLoadingVehicles || isLoadingVehicleClasses}>
            <Template.Header>
                <Template.Title>
                    Veículos
                </Template.Title>
                <div className="flex gap-2">
                    <Button target="success" onClick={exportData}><FaFileExcel/>Exportar</Button>
                    <Button onClick={openForm}><FaPlus/>Adicionar veículo</Button>
                </div>
            </Template.Header>
            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body>
                        <div className="flex gap-2 flex-col flex-wrap">
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-2">
                                    <InputForm
                                        id="prefix"
                                        label="Prefixo"
                                        value={filterValues.prefix}
                                        onChange={handleInput}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <SelectComponent
                                        id="status"
                                        label="Status"
                                        options={vehicleStatusArr}
                                        value={vehicleStatusArr.find(status => status.value === filterValues?.status) ?? "" }
                                        onChange={(option) => handleSpecialInput("status", option?.value ?? null)}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <InputForm
                                        id="plate"
                                        label="Placa"
                                        value={filterValues.plate}
                                        onChange={handleInput}
                                        inputClass="uppercase"
                                        mask={MaskDefault.plate}
                                    />
                                </div>
                                <div className="col-span-3">
                                    <InputForm
                                        id="brand"
                                        label="Marca"
                                        value={filterValues.brand}
                                        onChange={handleInput}
                                    />
                                </div>
                                <div className="col-span-3">
                                    <InputForm
                                        id="model"
                                        label="Modelo"
                                        value={filterValues.model}
                                        onChange={handleInput}
                                    />
                                </div>
                                <div className="col-span-4">
                                    <SelectComponent
                                        id="type"
                                        label="Tipo"
                                        options={vehicleTypeArr}
                                        value={vehicleTypeArr.find(type => type.value === filterValues?.type) ?? ""}
                                        onChange={(option) => handleSpecialInput("type", option?.value ?? null)}
                                    />
                                </div>
                                <div className="col-span-4">
                                    <InputForm
                                        id="vehicle_class"
                                        label="Classe"
                                        value={filterValues.vehicle_class}
                                        onChange={handleInput}
                                    />
                                </div>
                                <div className="col-span-4 flex gap-4">
                                    <div className="w-full">
                                        <SelectComponent
                                            id="next_km_revision"
                                            label="Manutenção"
                                            options={vehicleMaintenanceStatusArr}
                                            value={vehicleMaintenanceStatusArr.find(status => status.value === filterValues?.next_km_revision) ?? "" }
                                            onChange={(option) => handleSpecialInput("next_km_revision", option?.value ?? null)}
                                        />
                                    </div>
                                    <div className="gap-2 flex">
                                        <Button onClick={() => clean()} target="outline-primary" title="Limpar"><FaEraser/></Button>
                                        <Button onClick={() => search()} title="Buscar"><FaSearch/></Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="aggrid-row-pointer h-[calc(100vh-201px)]">
                            <AgGridReact
                                onGridReady={(params) => {
                                    gridApi.current = params.api
                                }}
                                rowData={vehicles}
                                onRowClicked={onRowClicked}
                                columnDefs={columnDefs}
                                onColumnMoved={onColumnMoved}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                animateRows={false}
                                pagination
                                paginationPageSize={50}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme({noHover: true})}
                            />
                        </div>
                        <ShowHideColumnsGrid
                            columnDefs={columnDefs}
                            toggleColumn={toggleColumn}
                            storageItem={storageItem}
                        />
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}

export default VehiclesList;